import { useCallback, useEffect, useRef, useState } from "react";
import { useLang } from "../hooks/useLang";
import { getMe, type Me, type PaymentProvider } from "../services/api";
import { reconcilePayments, startCheckout } from "../services/payments";
import PaymentMethodModal from "./PaymentMethodModal";
import BankTransferPanel from "./BankTransferPanel";

// Tek işlemde alınabilecek üst kredi sınırı (backend payment.go ile uyumlu).
// 1 GSPR üretimi ~1000 kredi olduğundan doküman-ölçekli (~100 doküman).
const MAX_CREDITS = 100000;
// En uygun (minimum) paket: 100 kredi; satın alım 100'lük adımlarla.
// Backend'deki eşikle BİRLİKTE değişir (payment.go::MinCheckoutCredits);
// ikisi ayrışırsa panel gönderir, API 400 döner.
const MIN_CREDITS = 100;
const STEP_CREDITS = 100;

// CreditsPanel — kredi bakiyesi + kredi yükleme. Kredi genel birimdir; GSPR
// üretimi (Dokümanı Oluştur) kredi maliyeti kadar (varsayılan 1000) düşer.
export default function CreditsPanel() {
  const { t } = useLang();
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(false);
  const [paying, setPaying] = useState<PaymentProvider | null>(null);
  const [qty, setQty] = useState(1000);
  const [err, setErr] = useState<string | null>(null);
  // pickerOpen — "Öde" tıklanınca açılan yöntem seçim modalı.
  // paying — hangi yöntemle ödeme başlatılıyor (o kart "başlatılıyor" gösterir).
  const [pickerOpen, setPickerOpen] = useState(false);
  // tab — "online" (kart + PayPal) | "transfer" (havale/EFT). Adet sekmelerin
  // DIŞINDA kaldığı için sekme değiştirmek girilen adedi korur.
  const [tab, setTab] = useState<"online" | "transfer">("online");
  const popupRef = useRef<Window | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      // Önce mutabakat: PayPal'da onaylayıp pencereyi kapatan kullanıcının kredisi
      // burada tanımlanır (return_url hiç gelmemiş olabilir). Bekleyen ödeme yoksa
      // maliyeti tek sorgu; hata durumunda sessizce geçer ve bakiye yine okunur.
      await reconcilePayments();
      setMe(await getMe());
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  // Popup ödeme sonrası success/error sayfamıza döner → postMessage ile haber verir.
  useEffect(() => {
    const onMsg = (e: MessageEvent) => { if (e.data === "payment:done") void load(); };
    const onFocus = () => { if (popupRef.current) void load(); };
    window.addEventListener("message", onMsg);
    window.addEventListener("focus", onFocus);
    return () => {
      window.removeEventListener("message", onMsg);
      window.removeEventListener("focus", onFocus);
    };
  }, [load]);

  // onPick — modaldaki yöntem kartına tıklanınca. Buradaki tıklama bir KULLANICI
  // JESTİ olduğu için window.open senkron çağrılabilir; modalın açılması jesti
  // tüketmez. (await'ten sonra açılsaydı tarayıcı popup'ı engellerdi.)
  const onPick = useCallback(async (provider: PaymentProvider) => {
    setErr(null);
    const n = Math.max(MIN_CREDITS, Math.min(MAX_CREDITS, Math.floor(qty) || MIN_CREDITS));
    const popup = window.open("about:blank", "medreg_pay", "width=480,height=760");
    if (!popup) { setErr(t("cr_popup_blocked")); setPickerOpen(false); return; }
    try {
      popup.document.write("<!doctype html><meta charset=utf-8><title>…</title><p style='font:15px sans-serif;padding:24px;color:#333'>Ödeme sayfasına yönlendiriliyorsunuz…</p>");
      popup.document.close(); // ÖNEMLİ: açık parse durumunda location ataması navigasyona dönmez
    } catch { /* cross-origin/edge — yoksay */ }
    popupRef.current = popup;
    setPaying(provider);
    try {
      const { pay_url } = await startCheckout(n, provider);
      if (!pay_url) throw new Error("pay_url alınamadı");
      popup.location.replace(pay_url);
      setPickerOpen(false); // yönlendirme başladı — modal kapanabilir
    } catch (e) {
      try { popup.close(); } catch { /* ignore */ }
      // Hata modalda değil kartın üstünde gösterilir: modal kapanır, kullanıcı
      // sebebi görür ve adedi düzeltip tekrar deneyebilir.
      setPickerOpen(false);
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setPaying(null);
    }
  }, [qty, t]);

  const balance = me?.credit_balance ?? 0;
  const unitTL = me?.doc_price_tl ?? 0;
  const unitUSD = me?.doc_price_usd ?? 0;
  // Açık ödeme yöntemleri backend'den gelir (yapılandırılmamış olan hiç görünmez).
  const methods: PaymentProvider[] = me?.payment_providers?.length ? me.payment_providers : ["garanti"];
  // Fiyatlardan biri tanımlıysa satın alma açılır; hangi yöntemin hazır olduğu
  // modaldaki kartta belli olur (Garanti kur ister, PayPal istemez).
  const canBuy = unitUSD > 0 || unitTL > 0;
  // GSPR üretiminin kredi maliyeti (backend belirler; varsayılan 1000).
  const cost = me?.price_gspr_generation_credits && me.price_gspr_generation_credits > 0 ? me.price_gspr_generation_credits : 1000;
  const qtyClamped = Math.max(MIN_CREDITS, Math.min(MAX_CREDITS, Math.floor(qty) || MIN_CREDITS));
  // Toplam tutar BACKEND ile BİREBİR AYNI formülden hesaplanır: yuvarlama en
  // sonda, yalnızca bir kez. Eskiden `unitTL × adet` yapılıyordu; unitTL zaten
  // 2 haneye yuvarlanmış olduğu için hata adetle çarpılıyordu — 1000 kredide
  // ~4 TL fark ediyor ve bankanın tahsil ettiği tutar panelde yazandan farklı
  // görünüyordu (müşteri bulgusu 2026-07-30).
  // Backend: round2(DocPriceUSD × credits × USDTRYRate) — payment.go.
  const totalTL = Math.round((me?.doc_price_usd ?? 0) * (me?.usd_try_rate ?? 0) * qtyClamped * 100) / 100;
  // PayPal'da kur devreye girmez: backend round2(DocPriceUSD × credits) tahsil eder.
  const totalUSD = Math.round((me?.doc_price_usd ?? 0) * qtyClamped * 100) / 100;
  // Yöntem henüz seçilmediği için kartta TL (birincil pazar) yazar, yanında USD
  // karşılığı durur. Fiilen tahsil edilecek tutar modalda yöntem başına gösterilir.
  // Yalnız PayPal açıksa TL'nin anlamı yok — doğrudan USD yazılır.
  const onlyPayPal = methods.length === 1 && methods[0] === "paypal";
  const totalText = onlyPayPal || unitTL <= 0
    ? `${totalUSD.toFixed(2)} USD`
    : `${totalTL.toFixed(2)} TL`;
  // EUR yalnız HAVALE için: hesaplarımızda EUR hesabı da var. Kur tanımsızsa 0
  // kalır ve havale bölümü EUR tutarını hiç yazmaz (yanlış tutar yazmaktansa hiç yazma).
  const totalEUR = Math.round((me?.doc_price_usd ?? 0) * (me?.usd_eur_rate ?? 0) * qtyClamped * 100) / 100;
  const docsFromQty = Math.floor(qtyClamped / cost);
  const docsFromBalance = Math.floor(balance / cost);
  const hasBank = !!me?.bank_accounts && me.bank_accounts.accounts.length > 0;

  // onlinePane — kart/PayPal sekmesinin gövdesi. Sekme yoksa (havale bilgisi
  // gelmediyse) doğrudan basılır; iki yerde tekrar etmesin diye değişkende.
  const onlinePane = (
    <>
      <div className="topup-total">
        <span className="topup-total-label">{t("cr_total")}</span>
        <span className="topup-total-value">{totalText}</span>
      </div>
      {/* Tek yöntem açıkken modal tek seçenek gösterip boşuna bir tıklama
          ekler — doğrudan o yöntemle başlat. Seçim ancak gerçekten
          seçenek varsa sorulur. */}
      <button
        className="btn-pri topup-btn"
        onClick={() => (methods.length > 1 ? setPickerOpen(true) : onPick(methods[0]))}
        disabled={paying !== null}
      >
        {paying ? t("cr_starting") : t("cr_buy_btn")}
      </button>
      <p className="hint topup-secure">{t("cr_secure_note")}</p>
    </>
  );

  return (
    <>
      <p className="hint">{t("cr_intro")}</p>
      {err && <div className="error">{t("err_prefix")}: {err}</div>}

      <section className="settings-section">
        <h3>{t("cr_balance_title")}</h3>
        {loading ? (
          <div className="hint">{t("cr_loading")}</div>
        ) : (
          <>
            <div style={{ fontSize: 30, fontWeight: 800, letterSpacing: "-0.02em" }}>
              {balance}
              <span className="hint" style={{ fontSize: 13, fontWeight: 400, marginLeft: 10 }}>
                {t("cr_balance_unit")}
              </span>
            </div>
            <p className="hint" style={{ marginTop: 6 }}>
              {balance > 0 ? `${t("cr_docs_equiv").replace("{n}", String(docsFromBalance))} · ${t("cr_balance_ok")}` : t("cr_balance_zero")}
            </p>

            {canBuy ? (
              <div className="topup-card">
                <h3 className="topup-title">{t("cr_buy_title")}</h3>
                <p className="hint topup-unit">
                  {onlyPayPal || unitTL <= 0 ? (
                    <>
                      {t("cr_unit_price")}: <strong>{unitUSD.toFixed(2)} USD</strong>
                      <span className="muted"> / {t("cr_balance_unit")}</span>
                    </>
                  ) : (
                    <>
                      {t("cr_unit_price")}: <strong>{unitTL.toFixed(2)} TL</strong>
                      <span className="muted"> (≈ {unitUSD.toFixed(2)} USD) / {t("cr_balance_unit")}</span>
                    </>
                  )}
                </p>

                <div className="topup-row">
                  <span className="topup-qty-label">{t("cr_qty")}</span>
                  <div className="qty-stepper" role="group" aria-label={t("cr_qty")}>
                    <button type="button" className="qty-btn" aria-label="−" disabled={qty <= MIN_CREDITS}
                      onClick={() => setQty((q) => Math.max(MIN_CREDITS, (Math.floor(q) || MIN_CREDITS) - STEP_CREDITS))}>−</button>
                    <input
                      className="qty-input" type="number" min={MIN_CREDITS} max={MAX_CREDITS} step={STEP_CREDITS}
                      value={qty}
                      onChange={(e) => setQty(Math.max(MIN_CREDITS, Math.min(MAX_CREDITS, parseInt(e.target.value, 10) || MIN_CREDITS)))}
                    />
                    <button type="button" className="qty-btn" aria-label="+" disabled={qty >= MAX_CREDITS}
                      onClick={() => setQty((q) => Math.min(MAX_CREDITS, (Math.floor(q) || MIN_CREDITS) + STEP_CREDITS))}>+</button>
                  </div>
                </div>
                <p className="hint" style={{ margin: "6px 0 0", fontSize: 12.5 }}>
                  {t("cr_min_note").replace("{n}", String(MIN_CREDITS))} · {t("cr_docs_equiv").replace("{n}", String(docsFromQty))} · {t("cr_doc_cost").replace("{c}", String(cost))}
                </p>

                {/* ADET SEKMELERİN DIŞINDA: iki ödeme yolu da aynı adedi
                    kullanıyor. Sekme başına ayrı stepper koymak kullanıcıyı
                    aynı sayıyı iki kez girmeye zorlardı. */}
                {hasBank ? (
                  <div className="pay-tabs">
                    <div className="pay-tablist" role="tablist" aria-label={t("cr_pay_how")}>
                      {(["online", "transfer"] as const).map((k, i, arr) => (
                        <button
                          key={k}
                          role="tab"
                          id={`paytab-${k}`}
                          aria-selected={tab === k}
                          aria-controls={`paypanel-${k}`}
                          tabIndex={tab === k ? 0 : -1}
                          className={"pay-tab" + (tab === k ? " is-on" : "")}
                          onClick={() => setTab(k)}
                          onKeyDown={(e) => {
                            // Ok tuşlarıyla gezinme — WAI-ARIA sekme deseni.
                            if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
                            e.preventDefault();
                            const d = e.key === "ArrowRight" ? 1 : -1;
                            const next = arr[(i + d + arr.length) % arr.length];
                            setTab(next);
                            document.getElementById(`paytab-${next}`)?.focus();
                          }}
                        >
                          {k === "online" ? t("cr_tab_online") : t("cr_tab_transfer")}
                        </button>
                      ))}
                    </div>

                    <div role="tabpanel" id="paypanel-online" aria-labelledby="paytab-online" hidden={tab !== "online"}>
                      {onlinePane}
                    </div>
                    <div role="tabpanel" id="paypanel-transfer" aria-labelledby="paytab-transfer" hidden={tab !== "transfer"}>
                      <BankTransferPanel
                        bank={me!.bank_accounts!}
                        credits={qtyClamped}
                        totalTL={totalTL}
                        totalUSD={totalUSD}
                        totalEUR={totalEUR}
                      />
                    </div>
                  </div>
                ) : (
                  // Havale bilgisi yoksa sekmeye gerek yok — tek yol var.
                  onlinePane
                )}
              </div>
            ) : (
              <p className="hint" style={{ marginTop: 12 }}>{t("cr_price_unset")}</p>
            )}
          </>
        )}
      </section>

      <PaymentMethodModal
        open={pickerOpen}
        methods={methods}
        credits={qtyClamped}
        unitUSD={unitUSD}
        usdTryRate={me?.usd_try_rate ?? 0}
        busy={paying}
        onPick={onPick}
        onClose={() => setPickerOpen(false)}
      />
    </>
  );
}
